import React, { useEffect, useState } from 'react'

import { Autocomplete, TextField, Tooltip, Chip, InputAdornment } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useDebounce } from 'use-debounce'

import AppLayout from '../../components/Layouts/AppLayout'
import AddButton from '../../components/AddButton'
import Loading from '../../components/Loading'
import ServerTable from '../../components/ServerTable'
import axios from '../../lib/axios'
import { useAuth } from '../../hooks/auth'
import { useStateContext } from '../../context/ContextProvider'
import { handleAxiosError, isValid, formatCurrency } from '../../helpers/helper'
import { buildUrl } from '../../helpers/apiHelper'

const searchInputSx = {
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 'none !important' },
    background: '#F5F5F5',
    borderRadius: '6px',
}

const WorkCenter = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { user } = useAuth({ middleware: 'guest' })
    const { config, choosesite, setChoosesite, pusher } = useStateContext()

    const [isLoading, setIsLoading] = useState(false)
    const [workCenters, setWorkCenters] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [random, setRandom] = useState(null)
    const [model, setModel] = useState({
        work_center_code: true,
        work_center_name: true,
        site_name: true,
        work_center_type: true,
        available_hours: true,
        cost_per_hour: true,
        status: true,
        actions: true
    })

    /* filter options */
    const [statusOptions, setStatusOptions] = useState([])
    const [typeOptions, setTypeOptions] = useState([])

    /* filters - restored from localStorage so they survive leaving/reopening this page */
    const [filterCode, setFilterCode] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.workcenter_code || ''
        }
        return ''
    })
    const [filterName, setFilterName] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.workcenter_name || ''
        }
        return ''
    })
    const [filterType, setFilterType] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.workcenter_type || ''
        }
        return ''
    })
    const [filterStatus, setFilterStatus] = useState(() => {
        if (localStorage.getItem('filters') !== null) {
            const filter = JSON.parse(localStorage.getItem('filters'))
            return filter[0]?.workcenter_status || ''
        }
        return ''
    })

    const [debouncedCode] = useDebounce(filterCode, 500)
    const [debouncedName] = useDebounce(filterName, 500)

    useEffect(() => {
        getTableView()
    }, [])

    /* site follows the global site chosen in the navigation, same as Forecasts/Routings */
    useEffect(() => {
        setChoosesite(localStorage.getItem('site'))
    }, [])

    useEffect(() => {
        getStatusOptions()
        getTypeOptions()
    }, [])

    useEffect(() => {
        if (isValid(choosesite)) {
            getWorkCenters()
        }
    }, [debouncedCode, debouncedName, choosesite, filterType, filterStatus, page, pageSize, random])

    /* persist filters to localStorage so they survive leaving/reopening this page */
    const createFilterArray = (code, name, type, status) => {
        if (localStorage.getItem('filters') === null) {
            localStorage.setItem('filters', JSON.stringify([{}]))
        }

        const filters = JSON.parse(localStorage.getItem('filters'))

        filters[0].workcenter_code = code
        filters[0].workcenter_name = name
        filters[0].workcenter_type = type
        filters[0].workcenter_status = status

        localStorage.setItem('filters', JSON.stringify(filters))
    }

    useEffect(() => {
        createFilterArray(filterCode, filterName, filterType, filterStatus)
    }, [filterCode, filterName, filterType, filterStatus])

    /* real-time updates: any create/update/delete of a work center refreshes the list for everyone */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')

        const channelCreated = pusher.subscribe(`${clientId}-workcenter-created`)
        const channelUpdated = pusher.subscribe(`${clientId}-workcenter-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-workcenter-deleted`)

        channelCreated.bind(`${clientId}-workcenter-created-event`, data => {
            setRandom(Math.random())
        })

        channelUpdated.bind(`${clientId}-workcenter-updated-event`, data => {
            setRandom(Math.random())
        })

        channelDeleted.bind(`${clientId}-workcenter-deleted-event`, data => {
            setRandom(Math.random())
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-workcenter-created`)
            pusher.unsubscribe(`${clientId}-workcenter-updated`)
            pusher.unsubscribe(`${clientId}-workcenter-deleted`)
        })
    }, [])

    const getTableView = async () => {
        await axios.get(`/api/get-table-view?view_name=work_centers_table&user_id=${user?.id}`, config)
            .then(res => {
                const savedModel = res.data
                if (Object.keys(savedModel?.success?.data ?? {}).length > 0) {
                    setModel(savedModel.success.data)
                }
            })
            .catch(({ response }) => {
                console.error(response)
            })
    }

    const getStatusOptions = async () => {
        await axios
            .get('/api/work-center-statuses', config)
            .then(res => {
                setStatusOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getTypeOptions = async () => {
        await axios
            .get('/api/work-center-types', config)
            .then(res => {
                setTypeOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getWorkCenters = async () => {
        setIsLoading(true)

        const url = buildUrl(
            '/api/work-centers',
            {
                where: {
                    work_center_code: debouncedCode,
                    work_center_name: debouncedName,
                    site_id: choosesite,
                    work_center_type: filterType,
                    status: filterStatus,
                    page: page + 1,
                    page_size: pageSize
                }
            }
        )

        await axios
            .get(url, config)
            .then(res => {
                setWorkCenters(res.data?.data ?? [])
                setTotal(res.data?.total ?? 0)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const handleDelete = async (row) => {
        const isConfirm = await Swal.fire({
            title: `${t('title_delete--long', { x: t('work_center').toLocaleLowerCase(), y: row.work_center_name })}`,
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,
            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => result.isConfirmed)

        if (!isConfirm) return

        await axios.delete(`/api/work-centers/${row.id}`, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })
            getWorkCenters()
        }).catch(({ response }) => {
            handleAxiosError({ response: response, showDescription: true })
        })
    }

    const typeLabel = (value) => typeOptions.find(o => o.value === value)?.label ?? value
    const statusLabel = (value) => statusOptions.find(o => o.value === value)?.label ?? value

    const columns = [
        { field: 'work_center_code', headerName: 'Code', flex: 0.7, minWidth: 130 },
        { field: 'work_center_name', headerName: 'Name', flex: 1, minWidth: 180 },
        { field: 'site_name', headerName: t('site'), flex: 0.8, minWidth: 150 },
        {
            field: 'work_center_type',
            headerName: t('type'),
            flex: 0.7,
            minWidth: 130,
            valueFormatter: ({ value }) => typeLabel(value)
        },
        { field: 'available_hours', headerName: 'Available Hours/Day', flex: 0.7, minWidth: 140 },
        {
            field: 'cost_per_hour',
            headerName: 'Cost ($) / Hour',
            flex: 0.7,
            minWidth: 130,
            valueFormatter: ({ value }) => isValid(value) ? `$${formatCurrency(value)}` : ''
        },
        {
            field: 'status',
            headerName: t('status'),
            flex: 0.6,
            minWidth: 120,
            renderCell: (params) => (
                <Chip
                    label={statusLabel(params.value)}
                    size='small'
                    sx={{
                        backgroundColor: String(params.value).toLowerCase() === 'active' ? '#E3F8EF' : '#F5F5F5',
                        color: String(params.value).toLowerCase() === 'active' ? '#00B78E' : '#88909C',
                        fontWeight: 600
                    }}
                />
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            flex: 0.6,
            minWidth: 130,
            renderCell: (params) => (
                <div className='flex gap-1'>
                    <Tooltip disableInteractive title={t('view')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => navigate(`${params.row.id}`)}><i className="fa-solid fa-eye"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('update')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => navigate(`${params.row.id}/edit`)}><i className="fa-solid fa-pencil"></i></span>
                        </div>
                    </Tooltip>
                    <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => handleDelete(params.row)}><i className="fa-solid fa-trash"></i></span>
                        </div>
                    </Tooltip>
                </div>
            )
        }
    ]

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout>
                <div className='p-6 flex w-full items-start justify-between gap-5'>
                    <div className='w-full bg-white rounded-md'>
                        <div className='p-5 flex justify-between items-center border-b border-[#EEEFF2]'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('work_centers')}</p>
                            <Tooltip disableInteractive title="Create new work center" placement='bottom'>
                                <div>
                                    <AddButton onClick={() => navigate('create')}><i className="fa-solid fa-plus text-[#333333]"></i></AddButton>
                                </div>
                            </Tooltip>
                        </div>

                        <div className='p-5 border-b border-[#EEEFF2]'>
                            <div className='flex-row flex justify-between items-end w-full gap-3'>
                                <div className='w-full'>
                                    <TextField
                                        fullWidth
                                        placeholder="Search by Work Center Code"
                                        value={filterCode}
                                        onChange={(e) => { setFilterCode(e.target.value); setPage(0) }}
                                        sx={searchInputSx}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <SearchIcon sx={{ color: 'rgba(0,0,0,.54)' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </div>
                                <div className='w-full'>
                                    <TextField
                                        fullWidth
                                        placeholder="Search by Work Center Name"
                                        value={filterName}
                                        onChange={(e) => { setFilterName(e.target.value); setPage(0) }}
                                        sx={searchInputSx}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <SearchIcon sx={{ color: 'rgba(0,0,0,.54)' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </div>
                                <div className='w-full'>
                                    <Autocomplete
                                        disablePortal
                                        options={typeOptions}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={typeOptions.find(o => o.value === filterType) || null}
                                        onChange={(e, value) => { setFilterType(value?.value || ''); setPage(0) }}
                                        sx={searchInputSx}
                                        renderInput={(params) => <TextField {...params} placeholder="Search by Type" />}
                                    />
                                </div>
                                <div className='w-full'>
                                    <Autocomplete
                                        disablePortal
                                        options={statusOptions}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={statusOptions.find(o => o.value === filterStatus) || null}
                                        onChange={(e, value) => { setFilterStatus(value?.value || ''); setPage(0) }}
                                        sx={searchInputSx}
                                        renderInput={(params) => <TextField {...params} placeholder={t('search_by_status')} />}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='pt-3'>
                            <ServerTable
                                columns={columns}
                                rowsPerPageOptions={[10, 15, 30]}
                                items={workCenters}
                                total={total}
                                setPage={setPage}
                                setPageSize={setPageSize}
                                page={page}
                                pageSize={pageSize}
                                columnVisibilityModel={model}
                                setModel={setModel}
                                view='work_centers'
                            />
                        </div>
                    </div>
                </div>
            </AppLayout>
        </>
    )
}

export default WorkCenter
